import { email, required, useTranslate } from "ra-core";
import { Separator } from "@/components/ui/separator";
import { useIsMobile } from "@/hooks/use-mobile";
import { BooleanInput } from "@/components/admin/boolean-input";
import { DateInput } from "@/components/admin/date-input";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceInput } from "@/components/admin/reference-input";
import { TextInput } from "@/components/admin/text-input";
import { SelectInput } from "@/components/admin/select-input";
import { ArrayInput } from "@/components/admin/array-input";
import { SimpleFormIterator } from "@/components/admin/simple-form-iterator";

import type { Sale } from "../types";
import {
  CAN_LOAD_CHOICES,
  COLLECTION_METHOD_CHOICES,
  LEAD_CHANNEL_CHOICES,
  LEAD_DIRECTION_CHOICES,
  LEAD_SOURCE_CHOICES,
  LEAD_STATUS_CHOICES,
  LEAD_TYPE_CHOICES,
  PREFERRED_LANGUAGE_CHOICES,
  REASON_FOR_DEAD_CHOICES,
} from "./leadConsts";

const personalInfoTypes = [
  { id: "Work", name: "Work" },
  { id: "Home", name: "Home" },
  { id: "Mobile", name: "Mobile" },
  { id: "Other", name: "Other" },
];

export const LeadInputs = () => {
  const isMobile = useIsMobile();

  return (
    <div className="flex flex-col gap-2 p-1">
      <div className="flex gap-10 md:gap-6 flex-col md:flex-row">
        <div className="flex flex-col gap-10 flex-1">
          <LeadIdentityInputs />
          <LeadContactInputs />
          <LeadAddressInputs />
        </div>
        {isMobile ? null : (
          <Separator orientation="vertical" className="flex-shrink-0" />
        )}
        <div className="flex flex-col gap-10 flex-1">
          <LeadQualificationInputs />
          <LeadBusinessInputs />
          <LeadMiscInputs />
        </div>
      </div>
    </div>
  );
};

const Section = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <div className="flex flex-col gap-4">
    <h6 className="text-lg font-semibold">{title}</h6>
    {children}
  </div>
);

const LeadIdentityInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.identity", { _: "Identity" })}>
      <TextInput source="salutation" helperText={false} />
      <TextInput source="first_name" validate={required()} helperText={false} />
      <TextInput source="last_name" validate={required()} helperText={false} />
      <TextInput source="account_name" helperText={false} />
      <TextInput source="account_description" multiline helperText={false} />
    </Section>
  );
};

const LeadContactInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.contact", { _: "Contact info" })}>
      <ArrayInput source="email_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="email"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.leads.fields.email", { _: "Email" })}
            validate={email()}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <ArrayInput source="phone_jsonb" helperText={false}>
        <SimpleFormIterator
          inline
          disableReordering
          disableClear
          className="[&>ul>li]:border-b-0 [&>ul>li]:pb-0"
        >
          <TextInput
            source="number"
            className="w-full"
            helperText={false}
            label={false}
            placeholder={translate("resources.leads.fields.phone_number", { _: "Phone number" })}
          />
          <SelectInput
            source="type"
            helperText={false}
            label={false}
            optionText="name"
            choices={personalInfoTypes}
            defaultValue="Work"
            className="w-24 min-w-24"
          />
        </SimpleFormIterator>
      </ArrayInput>
      <BooleanInput source="do_not_call" helperText={false} />
    </Section>
  );
};

const LeadAddressInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.address", { _: "Address" })}>
      <TextInput source="address" helperText={false} />
      <TextInput source="city" helperText={false} />
      <TextInput source="state_abbr" helperText={false} />
      <TextInput source="zipcode" helperText={false} />
      <TextInput source="country" helperText={false} />
    </Section>
  );
};

const LeadQualificationInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.qualification", { _: "Qualification" })}>
      <SelectInput
        source="status"
        choices={LEAD_STATUS_CHOICES}
        validate={required()}
        helperText={false}
      />
      <SelectInput
        source="reason_for_dead"
        choices={REASON_FOR_DEAD_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="lead_type"
        choices={LEAD_TYPE_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="lead_direction"
        choices={LEAD_DIRECTION_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="lead_source"
        choices={LEAD_SOURCE_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="lead_channel"
        choices={LEAD_CHANNEL_CHOICES}
        helperText={false}
      />
      <DateInput source="first_contact_date" helperText={false} />
    </Section>
  );
};

const LeadBusinessInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.business", { _: "Business details" })}>
      <SelectInput
        source="preferred_language"
        choices={PREFERRED_LANGUAGE_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="collection_method"
        choices={COLLECTION_METHOD_CHOICES}
        helperText={false}
      />
      <SelectInput
        source="can_load"
        choices={CAN_LOAD_CHOICES}
        helperText={false}
      />
      <NumberInput source="loads_per_month" helperText={false} />
      <TextInput source="tax_id" helperText={false} />
      <TextInput source="competitor" helperText={false} />
      <TextInput source="competitor_notes" multiline helperText={false} />
    </Section>
  );
};

const LeadMiscInputs = () => {
  const translate = useTranslate();
  return (
    <Section title={translate("resources.leads.field_categories.misc", { _: "Misc" })}>
      <TextInput
        source="lead_notes"
        multiline
        helperText={translate("resources.leads.fields.lead_notes_helper", {
          _: "Original inquiry text",
        })}
      />
      <TextInput source="description" multiline helperText={false} />
      <ReferenceInput
        reference="sales"
        source="sales_id"
        sort={{ field: "last_name", order: "ASC" }}
        filter={{ "disabled@neq": true }}
      >
        <SelectInput
          helperText={false}
          optionText={saleOptionRenderer}
          validate={required()}
        />
      </ReferenceInput>
    </Section>
  );
};

const saleOptionRenderer = (choice: Sale) =>
  `${choice.first_name} ${choice.last_name}`;
